import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { of, switchMap, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MenuItem } from '../models/menu-item.model';
import { CartService } from './cart.service';
import { MenuService } from './menu.service';
import { TableContextService } from './table-context.service';

const NOTICE_DURATION_MS = 6000;

/**
 * Runs app-wide (injected eagerly in App's constructor, like PromotionCalculationService) so a
 * stale sessionStorage cart gets checked against the live catalog on every full page load -
 * cart-page or checkout, not just menu-page - since TableContextService's storeId is itself
 * restored from sessionStorage before this ever runs.
 */
@Injectable({ providedIn: 'root' })
export class CartReconciliationService {
  private readonly cart = inject(CartService);
  private readonly menuService = inject(MenuService);
  private readonly tableContext = inject(TableContextService);

  private readonly _notice = signal<string | null>(null);
  readonly notice = this._notice.asReadonly();

  constructor() {
    toObservable(this.tableContext.storeId)
      .pipe(
        switchMap((storeId) =>
          storeId ? this.menuService.getMenuItems(storeId).pipe(catchError(() => of(null))) : of(null)
        )
      )
      .subscribe((catalog) => this.onCatalog(catalog));
  }

  private onCatalog(catalog: MenuItem[] | null): void {
    if (!catalog || this.cart.items().length === 0) {
      return;
    }
    const { removed, changed } = this.cart.reconcile(catalog);
    if (removed.length === 0 && changed.length === 0) {
      return;
    }
    const parts: string[] = [];
    if (removed.length > 0) {
      parts.push(
        `${removed.join(', ')} ${removed.length > 1 ? 'are' : 'is'} no longer available and ${removed.length > 1 ? 'were' : 'was'} removed from your cart`
      );
    }
    if (changed.length > 0) {
      parts.push(`Price updated for ${changed.join(', ')}`);
    }
    this._notice.set(parts.join('. '));
    timer(NOTICE_DURATION_MS).subscribe(() => this._notice.set(null));
  }

  dismiss(): void {
    this._notice.set(null);
  }
}
